import { Component, OnInit, Input, OnDestroy, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { map, switchMap, debounceTime, filter, finalize } from 'rxjs/operators';
import { ClientApiService } from 'src/app/services/client-api.service';

import { Document } from 'src/app/shared/document';

/**
 * Form panel for editing the buyer-side client fields on a document.
 *
 * The panel has two client groups:
 * - **CLI group** (`cliCli`, `cliPer`, `cliDep`) — the main buyer company, contact person,
 *   and primary stock department.  These fields drive `DOCCLIENTSID`, `DOCPERSONID`, and
 *   `DOCCLIDEPID` on the document.
 * - **PCO group** (`pcoCli`, `pcoPer`, `pcoDep`) — the legal entity for invoicing, the
 *   responsible person, and the corresponding PCO stock department.  These drive
 *   `DOCPCOCLIID`, `DOCPCOPERID`, and `DOCPCODEPID`.
 *
 * When the user selects a CLI company, a `clientId$` Subject pipeline fires
 * `getMainIdByClientId` to resolve `DOCCLIENTSID` (the master company in the hierarchy)
 * and writes both `DOCCLIENTSID` and `DOCPCOCLIID` on the document.
 *
 * When a CLI or PCO stock is selected, `getChildDepartmentsById` resolves the matching PCO
 * sub-department and writes `DOCPCODEPID`.
 *
 * Every mutation emits the modified `document` via the `changes` output.
 *
 * **Output:** `changes` emits the mutated `Document` after every field selection.
 */
@Component({
  selector: 'app-edit-document-client-inventory',
  templateUrl: './edit-document-client-inventory.component.html',
  styleUrls: ['./edit-document-client-inventory.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentClientInventoryComponent implements OnInit, OnChanges, OnDestroy {

  /** The document whose client fields are being edited; mutated in place on selection. */
  @Input() document: Document;

  /** Label for the main buyer company selector. */
  @Input() cliCliLabel: string = 'Основная организация покупателя';
  /** Label for the CLI contact-person selector. */
  @Input() cliPerLabel: string = 'Контактное лицо';
  /** Label for the CLI stock-department selector. */
  @Input() cliDepLabel: string = 'Основной склад';
  /** Label for the PCO legal-entity selector. */
  @Input() pcoCliLabel: string = 'Юридическое лицо покупателя';
  /** Label for the PCO responsible-person selector. */
  @Input() pcoPerLabel: string = 'Ответственный';
  /** Label for the PCO stock-department selector. */
  @Input() pcoDepLabel: string = 'Склад';

  cliCompanyId: number;
  /** Pushes selected CLI company IDs to trigger `getMainIdByClientId` resolution. */
  private clientId$ = new Subject<number>();
  private _depChildQuery: Subscription;

  private _cliListQuery: Subscription;
  private _mainIdQuery: Subscription;

  /** Emits the mutated `Document` after every field selection. */
  @Output() changes: EventEmitter<Document> = new EventEmitter();

  constructor(
    private cliApi: ClientApiService,
  ) { }

  /** Reacts to `document` changes — currently a no-op (body has a dead comparison). */
  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** EditDocumentClientComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentClientComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** EditDocumentClientComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {

        case 'document':

          if (this.cliCompanyId != change.currentValue.DOCCLIENTSID) {
            this.cliCompanyId != change.currentValue.DOCCLIENTSID;
          }

          break;
      }
    }
  }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentClientComponent in ngOnDestroy');

    if (this._mainIdQuery) { this._mainIdQuery.unsubscribe(); }
    if (this._depChildQuery) { this._depChildQuery.unsubscribe(); }
  }

  /** Sets up the `clientId$` → `getMainIdByClientId` pipeline for CLI company resolution. */
  ngOnInit(): void {
    console.log('*** EditDocumentClientComponent in ngOnInit');

    this._mainIdQuery =
      this.clientId$.pipe(
        debounceTime(200),
        switchMap(id => this.cliApi.getMainIdByClientId(id).pipe(
            map(mc => {
              console.log('*** EditDocumentClientComponent in clientId$ pipe/map: ' + JSON.stringify(mc));

              this.document.DOCCLIENTSID = (mc && mc[0]) ? mc[0].CLIID : null;
              this.document.DOCPCOCLIID = id;

              this.changes.emit(this.document);
            }))))

        .subscribe();
  }

  /** Handles CLI company selection — pushes the ID onto `clientId$` to resolve the master company. */
  cliCompanySelected(selectedId) {
    console.log('*** EditDocumentClientComponent in cliCompanySelected: ' + selectedId);

    this.clientId$.next(selectedId);
  }

  /**
   * Handles CLI contact-person selection — writes `DOCPERSONID` and, when CLI and PCO share the
   * same company, mirrors the person and department to `DOCPCOPERID` / `DOCPCODEPID`.
   */
  cliPersonSelected(selectedId) {
    /*
     * TODO: Select the EditDocumentClientComponent person in PCO group (have right to sign the doc)
     */
    console.log('*** EditDocumentClientComponent in cliPersonSelected: ' + selectedId);
    this.document.DOCPERSONID = selectedId;
    if (this.document.DOCCLIENTSID == this.document.DOCPCOCLIID) {
      this.document.DOCPCOPERID = this.document.DOCPERSONID;
      this.document.DOCPCODEPID = this.document.DOCCLIDEPID;
    }
    this.changes.emit(this.document);
  }

  /**
   * Handles CLI stock-department selection — writes `DOCCLIDEPID` and resolves the matching PCO
   * sub-department via `getChildDepartmentsById`, then emits `changes`.
   */
  cliStockSelected(selectedId) {
    /*
     * TODO: Select the corresponding department in PCO group (equal name)
     */
    console.log('*** EditDocumentClientComponent in cliStockSelected: ' + selectedId);
    this.document.DOCCLIDEPID = selectedId;

    this._depChildQuery =
      this.cliApi.getChildDepartmentsById(this.document.DOCCLIDEPID, this.document.DOCPCOCLIID).pipe(
        filter(data => data.length > 0),
        map(data => this.document.DOCPCODEPID = data[0].CLIID),
        finalize(() => this.changes.emit(this.document))
      )
        .subscribe();
  }

  /**
   * Handles PCO legal-entity selection — writes `DOCPCOCLIID` and resolves the matching PCO
   * sub-department via `getChildDepartmentsById`, then emits `changes`.
   */
  pcoCompanySelected(selectedId) {
    console.log('*** EditDocumentClientComponent in pcoCompanySelected: ' + selectedId);
    this.document.DOCPCOCLIID = selectedId;

    this._depChildQuery =
      this.cliApi.getChildDepartmentsById(this.document.DOCCLIDEPID, this.document.DOCPCOCLIID).pipe(
        filter(data => data.length > 0),
        map(data => this.document.DOCPCODEPID = data[0].CLIID),
        finalize(() => this.changes.emit(this.document))
      )
        .subscribe();
  }

  /** Handles PCO responsible-person selection — writes `DOCPCOPERID` and emits `changes`. */
  pcoPersonSelected(selectedId) {
    console.log('*** EditDocumentClientComponent in pcoPersonSelected: ' + selectedId);
    this.document.DOCPCOPERID = selectedId;
    this.changes.emit(this.document);
  }

  /** Handles PCO stock-department selection — writes `DOCPCODEPID` and emits `changes`. */
  pcoStockSelected(selectedId) {
    console.log('*** EditDocumentClientComponent in pcoStockSelected: ' + selectedId);
    this.document.DOCPCODEPID = selectedId;
    this.changes.emit(this.document);
  }
}
